import config from '@/config';
import commonUtils from '@/school-management/utils/common';
import {
  GroupsReportQueryParams,
  GroupsReportSortByQueryParams,
} from '@/school-management/types/response';
import type {
  TeacherAutocompleteItem,
  GroupAutocompleteItem,
  ClassCodeAutocompleteItem,
} from '@/school-management/types/response/autocomplete';

export const CLASSES_REPORT_PAGE_SIZE = config.groupsPageSize;

export type ClassFiltersRecord = Record<string, unknown> | undefined;

/**
 * Maps GenericTable / school-details UI sort keys to API sort fields.
 */
export function buildGroupsReportQueryParams(args: {
  page: number;
  pageSize?: number;
  sortByUi: string;
  sortDirection: 'asc' | 'desc';
  searchQuery?: string;
  classFilters?: ClassFiltersRecord;
  /** When set and no teacher filter in classFilters, restricts report to these teachers (teacher dashboard). */
  lockedTeacherIds?: string[];
  /** Grade filter from teacher GradesFilter when classFilters.grade is empty. */
  extraGradeIds?: number[];
  translateForGradeMapper: (key: string) => string;
}): GroupsReportQueryParams {
  const {
    page,
    pageSize = CLASSES_REPORT_PAGE_SIZE,
    sortByUi,
    sortDirection,
    searchQuery,
    classFilters,
    lockedTeacherIds,
    extraGradeIds,
    translateForGradeMapper,
  } = args;

  let sortByKey: GroupsReportSortByQueryParams = 'groupName';
  switch (sortByUi) {
    case 'name':
      sortByKey = 'groupName';
      break;
    case 'teacher':
      sortByKey = 'teacherName';
      break;
    case 'grade':
      sortByKey = 'gradeId';
      break;
    case 'students':
      sortByKey = 'numberOfStudents';
      break;
    case 'activeStudents':
      sortByKey = 'numberOfActiveStudents';
      break;
    default:
      sortByKey = 'groupName';
  }

  const queryParams: GroupsReportQueryParams = {
    page,
    pageSize,
  };

  if ((sortByUi || '').trim()) {
    queryParams.sortBy = sortByKey;
    queryParams.sortDirection = sortDirection;
  }

  const normalizedSearchQuery = searchQuery?.trim();
  if (normalizedSearchQuery) {
    queryParams.search = normalizedSearchQuery;
  }

  const cf = classFilters || {};

  if (cf.groupName) {
    const groupIds = (cf.groupName as GroupAutocompleteItem[]).map(g => g.groupId);
    if (groupIds.length > 0) {
      queryParams.groupIds = groupIds;
    }
  }

  if (cf.classCode) {
    const classCode = cf.classCode as ClassCodeAutocompleteItem[];
    if (classCode.length > 0) {
      queryParams.classCode = classCode;
    }
  }

  const teacherFromFilter =
    (cf.teacher as TeacherAutocompleteItem[] | undefined)?.map(te => te.id).filter(Boolean) ?? [];

  let teacherIds: string[] | undefined;
  if (teacherFromFilter.length > 0) {
    teacherIds = teacherFromFilter;
  } else if (lockedTeacherIds && lockedTeacherIds.length > 0) {
    teacherIds = lockedTeacherIds;
  }
  if (teacherIds && teacherIds.length > 0) {
    queryParams.teacherIds = teacherIds;
  }

  const grades = (cf.grade || []) as string[];
  if (grades.length > 0) {
    const gradesLabelToIdMapper = commonUtils.getGradesLabelToIdMapper(translateForGradeMapper);
    queryParams.gradeIds = grades.map(g => gradesLabelToIdMapper[g]);
  } else if (extraGradeIds && extraGradeIds.length > 0) {
    queryParams.gradeIds = extraGradeIds;
  }

  return queryParams;
}
